"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.utilizationViewSchema = exports.utilizationPersonSchema = exports.utilizationSkillSchema = exports.utilizationAllocationSchema = exports.utilizationBarSchema = exports.utilizationFiguresSchema = exports.utilizationColumnSchema = exports.UTILIZATION_COLORS = exports.UTILIZATION_KINDS = exports.UTILIZATION_GROUP_TOLERANCE = exports.UTILIZATION_MAX_DAYS = exports.UTILIZATION_MODES = void 0;
exports.utilizationBooked = utilizationBooked;
exports.utilizationLoad = utilizationLoad;
exports.utilizationPercent = utilizationPercent;
const zod_1 = require("zod");
const allocation_1 = require("../delivery/allocation");
const contract_1 = require("../people/contract");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · Utilizáció — the fourth dashboard. How much of everybody's  │
 * │ time is booked, and on what kind of work.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `Forecastify - Utilizacios riport (standalone).html`, `view: 'dashutil'`. Read-only, like the
 * other three: a stacked chart of *Kihasználtság* per column, then one table of people grouped by
 * their default role, their seniority or their skills — or the flat *Felszabaduló emberek* list.
 *
 * ⚠️⚠️ **THREE KINDS OF BOOKED HOUR, AND WHICH ONE AN HOUR IS IS DECIDED HERE ONCE**
 * (`decisions.md` UT-1):
 *
 *  * **soft** — any allocation booked `soft` (`0143`), whatever it is for. A pencilled-in hour is
 *    not a commitment, so it is drawn hatched and kept OUT of *Kiosztott*;
 *  * **internal** — a hard booking on a project whose client is *Belső projekt*, which the project
 *    dialog stores as no client at all (`0014`'s nullable `client_id`);
 *  * **billable** — every other hard booking.
 *
 * ⚠️⚠️ **ONE PERSON IS COUNTED ONCE, AS THEIR DEFAULT ROLE.** Somebody can be Frontend on one
 * project and Backend on another; the grouping and the role filter read `members.
 * default_project_role_id` — *Alap szerep* — so they appear in one group and the totals do not
 * double them. The role they hold ON a project is shown on that project's row, and differs from
 * the default as *más szerepkör*.
 *
 * ⚠️ **Only planned people** — `resource_planned`, the person screen's *Erőforrás* switch. Somebody
 * marked *Nem tervezett* is not in this report at all, exactly as they are not on Erőforrás.
 *
 * ⚠️ **Every figure is a STRING**, like every other `numeric` on the wire.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
exports.UTILIZATION_MODES = ['week', 'day'];
/** The mockup's own cap (`dnI(R.from) + 370`): a year and a few days, in either mode. */
exports.UTILIZATION_MAX_DAYS = 370;
/**
 * ⚠️ **A GROUP is *ok* from 95% of its capacity, a PERSON only at 100%.** The mockup's
 * `linesFor(list, 0.95)` against `linesFor([p], 1)`: a team at 96% is fully used, one person at 96%
 * still has an afternoon.
 */
exports.UTILIZATION_GROUP_TOLERANCE = 0.95;
/**
 * 26 Oct 2026 · `nonBillable` was `internal` — an internal PROJECT'S hours. The product owner: every
 * non-billable LINE ITEM's hours belong there, wherever the project; an internal project has only
 * non-billable line items (`internal_project_billable`), so it is still counted.
 */
exports.UTILIZATION_KINDS = ['billable', 'nonBillable', 'soft'];
/** The chart's series colours — the mockup's, and the legend's. */
exports.UTILIZATION_COLORS = {
    billable: '#4E75D9',
    nonBillable: '#59C7C7',
};
exports.utilizationColumnSchema = zod_1.z.object({
    key: zod_1.z.string(),
    from: wireDateSchema,
    to: wireDateSchema,
    /** The ISO week number in week mode; null in day mode. */
    week: zod_1.z.number().int().nullable(),
    current: zod_1.z.boolean(),
    /** Day mode: a weekend or a day the calendar takes away. Always false in week mode. */
    off: zod_1.z.boolean(),
});
/** One column's hours. `leave` is time away; `capacity` is what is left after it. */
exports.utilizationFiguresSchema = zod_1.z.object({
    capacity: zod_1.z.string(),
    billable: zod_1.z.string(),
    nonBillable: zod_1.z.string(),
    soft: zod_1.z.string(),
    leave: zod_1.z.string(),
});
/** One bar: the column's figures over everybody shown, plus the hard hours per project. */
exports.utilizationBarSchema = exports.utilizationFiguresSchema.extend({
    projects: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), color: zod_1.z.string(), hours: zod_1.z.string() })),
});
/**
 * One row under a person: what they are booked on, per project, per role held there, per kind.
 * ⚠️ Two line items of one project collapse into one row — the mockup's key is the project.
 */
exports.utilizationAllocationSchema = zod_1.z.object({
    key: zod_1.z.string(),
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    clientName: zod_1.z.string().nullable(),
    pmName: zod_1.z.string().nullable(),
    /** The project role they hold on THIS project, if their membership names one. */
    roleName: zod_1.z.string().nullable(),
    /** That role differs from their default one — the mockup's *más szerepkör*. */
    crossRole: zod_1.z.boolean(),
    kind: zod_1.z.enum(exports.UTILIZATION_KINDS),
    booking: zod_1.z.enum(allocation_1.ALLOCATION_BOOKINGS),
    hours: zod_1.z.array(zod_1.z.string()),
});
exports.utilizationSkillSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    levelName: zod_1.z.string().nullable(),
    levelColor: zod_1.z.string().nullable(),
});
exports.utilizationPersonSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    memberAvatarUrl: zod_1.z.string().nullable(),
    memberAvatarSeed: zod_1.z.string().nullable(),
    role: zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), color: zod_1.z.string() }).nullable(),
    seniority: zod_1.z.enum(contract_1.SENIORITY_LEVELS).nullable(),
    skills: zod_1.z.array(exports.utilizationSkillSchema),
    /** Per column, in order. */
    figures: zod_1.z.array(exports.utilizationFiguresSchema),
    allocations: zod_1.z.array(exports.utilizationAllocationSchema),
});
exports.utilizationViewSchema = zod_1.z.object({
    mode: zod_1.z.enum(exports.UTILIZATION_MODES),
    from: wireDateSchema,
    to: wireDateSchema,
    columns: zod_1.z.array(exports.utilizationColumnSchema),
    bars: zod_1.z.array(exports.utilizationBarSchema),
    people: zod_1.z.array(exports.utilizationPersonSchema),
    /** The filter's options: every project role, and the projects booked in the window. */
    roles: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), color: zod_1.z.string() })),
    projects: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string(), color: zod_1.z.string() })),
});
/** The hard hours — *Kiosztott*. Soft is deliberately not in it. */
function utilizationBooked(figures) {
    return Number(figures.billable) + Number(figures.nonBillable);
}
/**
 * The colour of a *Kiosztott* cell — the mockup's `allocKind`, with its 0.05 h of slack either side
 * so a rounding remainder does not paint a cell red.
 */
function utilizationLoad(booked, capacity, tolerance = 1) {
    if (capacity === 0 && booked === 0)
        return 'none';
    if (booked > capacity + 0.05)
        return 'over';
    if (booked >= capacity * tolerance - 0.05)
        return 'ok';
    return 'under';
}
/** Hours as a percentage of capacity; 0 when there is no capacity to measure against. */
function utilizationPercent(hours, capacity) {
    return capacity > 0 ? (hours / capacity) * 100 : 0;
}
//# sourceMappingURL=utilization.js.map